import './styles/PeriodsAndExtraInfo.scss';

import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  TextField
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';

import { Field, useFormikContext } from 'formik';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { translations } from 'locales/i18n';

import { CreateModuleSelectionInput } from '../../models';

export const PeriodsAndExtraInfo: React.FC = () => {
  const { values, setFieldValue, touched, errors, handleBlur } =
    useFormikContext<CreateModuleSelectionInput>();
  const { t } = useTranslation();

  const handleChangeMaxSelectedModules = event => {
    event.target.value < 0
      ? setFieldValue('maxSelectedModules', Math.abs(event.target.value))
      : setFieldValue('maxSelectedModules', event.target.value);
  };

  const handleChangeLengthOfTheWishlist = event => {
    event.target.value < 0
      ? setFieldValue('lengthOfTheWishlist', Math.abs(event.target.value))
      : setFieldValue('lengthOfTheWishlist', event.target.value);
  };

  const today = new Date();
  const [minEndDate, setMinEndDate] = useState<Date | undefined | null>();
  const [minDischargeByStudentUntil, setMinDischargeByStudentUntil] = useState<
    Date | undefined | null
  >();

  function handleStartChange(date: Date | null) {
    if (date && date.getHours() === 0 && date.getMinutes() === 0) {
      date.setMinutes(1);
    }
    setFieldValue('startDate', date);
    setMinEndDate(date);
  }

  function handleEndChange(date: Date | null) {
    if (date && date.getHours() === 0 && date.getMinutes() === 0) {
      date.setHours(23);
      date.setMinutes(59);
    }
    setFieldValue('endDate', date);
    setMinDischargeByStudentUntil(date);
    if (!values.dischargeByStudentsUntil || (date && date > values.dischargeByStudentsUntil)) {
      setFieldValue('dischargeByStudentsUntil', date);
    }
  }

  return (
    <Box p={2} className="periods-extra-info">
      <FormControl required>
        <FormLabel>
          {t(translations.pages.moduleSelections.create.periodsAndExtraInfo.electionTimeframe())}
        </FormLabel>
        <Box>
          <DateTimePicker
            renderInput={props => (
              <TextField
                {...props}
                id="startDate"
                required
                variant="outlined"
                error={touched.startDate && Boolean(errors.startDate)}
                helperText={touched.startDate && <>{errors.startDate}</>}
                onBlur={handleBlur}
                label={t(
                  translations.pages.moduleSelections.create.periodsAndExtraInfo.startDate()
                )}
              />
            )}
            disablePast={values?.endDate ? false : true}
            value={values.startDate}
            onChange={date => {
              handleStartChange(date);
            }}
            mask="__.__.____ __:__"
            minDate={today}
            inputFormat="dd.MM.yyyy HH:mm"
            ampm={false}
            ampmInClock={false}
          />

          <DateTimePicker
            renderInput={props => (
              <TextField
                {...props}
                id="endDate"
                required
                variant="outlined"
                error={touched.endDate && Boolean(errors.endDate)}
                helperText={touched.endDate && <>{errors.endDate}</>}
                onBlur={handleBlur}
                label={t(translations.pages.moduleSelections.create.periodsAndExtraInfo.endDate())}
              />
            )}
            disablePast={values?.startDate ? false : true}
            value={values.endDate}
            onChange={date => {
              handleEndChange(date);
            }}
            mask="__.__.____ __:__"
            minDate={minEndDate}
            inputFormat="dd.MM.yyyy HH:mm"
            ampm={false}
            ampmInClock={false}
          />
        </Box>
      </FormControl>

      <FormControl>
        <FormLabel>
          {t(translations.pages.moduleSelections.create.periodsAndExtraInfo.extraTimeframes())}
        </FormLabel>
        <Box>
          <DateTimePicker
            renderInput={props => (
              <TextField
                {...props}
                id="contentVisibleFrom"
                required
                variant="outlined"
                error={touched.contentVisibleFrom && Boolean(errors.contentVisibleFrom)}
                helperText={touched.contentVisibleFrom && <>{errors.contentVisibleFrom}</>}
                onBlur={handleBlur}
                label={t(
                  translations.pages.moduleSelections.create.periodsAndExtraInfo.contentVisibleFrom()
                )}
              />
            )}
            disablePast={values?.contentVisibleFrom ? false : true}
            value={values.contentVisibleFrom}
            onChange={e => setFieldValue('contentVisibleFrom', e)}
            mask="__.__.____ __:__"
            minDate={today}
            inputFormat="dd.MM.yyyy HH:mm"
            ampm={false}
            ampmInClock={false}
          />

          <DateTimePicker
            renderInput={props => (
              <TextField
                {...props}
                id="dischargeByStudentsUntil"
                variant="outlined"
                onBlur={handleBlur}
                error={touched.dischargeByStudentsUntil && Boolean(errors.dischargeByStudentsUntil)}
                helperText={
                  touched.dischargeByStudentsUntil && <>{errors.dischargeByStudentsUntil}</>
                }
                label={t(
                  translations.pages.moduleSelections.create.periodsAndExtraInfo.dischargeByStudentsUntil()
                )}
              />
            )}
            disablePast={values?.dischargeByStudentsUntil ? false : true}
            value={values.dischargeByStudentsUntil}
            mask="__.__.____ __:__"
            minDate={minDischargeByStudentUntil}
            onChange={e => setFieldValue('dischargeByStudentsUntil', e)}
            inputFormat="dd.MM.yyyy HH:mm"
            ampm={false}
            ampmInClock={false}
          />
        </Box>
      </FormControl>
      <Grid container spacing={2} justifyContent="flex-start">
        <Grid item>
          <FormControl required>
            <Grid item>
              <FormLabel style={{ fontWeight: 'bold', fontSize: '20px', color: '#000' }}>
                {t(
                  translations.pages.moduleSelections.create.periodsAndExtraInfo.maxSelectedModules()
                )}
              </FormLabel>
            </Grid>
            <Field
              as={TextField}
              name="maxSelectedModules"
              id="maxSelectedModules"
              type="number"
              variant="outlined"
              required
              className="number-input"
              style={{ width: '237px' }}
              value={values.maxSelectedModules}
              error={touched.maxSelectedModules && Boolean(errors.maxSelectedModules)}
              helperText={touched.maxSelectedModules && errors.maxSelectedModules}
              onBlur={handleBlur}
              onChange={handleChangeMaxSelectedModules}
              InputLabelProps={{
                shrink: true
              }}
            />
          </FormControl>
        </Grid>
        <Grid item>
          <FormControl required>
            <Grid item>
              <FormLabel style={{ fontWeight: 'bold', fontSize: '20px', color: '#000' }}>
                {t(
                  translations.pages.moduleSelections.create.periodsAndExtraInfo.lengthOfTheWishlist()
                )}
              </FormLabel>
            </Grid>
            <Field
              as={TextField}
              name="lengthOfTheWishlist"
              id="lengthOfTheWishlist"
              type="number"
              variant="outlined"
              required
              className="number-input"
              style={{ width: '237px' }}
              value={values.lengthOfTheWishlist}
              error={touched.lengthOfTheWishlist && Boolean(errors.lengthOfTheWishlist)}
              helperText={touched.lengthOfTheWishlist && errors.lengthOfTheWishlist}
              onBlur={handleBlur}
              onChange={handleChangeLengthOfTheWishlist}
              InputLabelProps={{
                shrink: true
              }}
            />
          </FormControl>
        </Grid>
      </Grid>
      {values?.subjectType === 'AWPM' && (
        <FormControlLabel
          control={
            <Checkbox
              checked={values.activeInAllCommunities}
              onChange={v => {
                setFieldValue('activeInAllCommunities', v.target.checked);
              }}
              name="activeInAllCommunities"
              color="primary"
            />
          }
          label={t(
            translations.pages.moduleSelections.create.periodsAndExtraInfo.activeInAllCommunities()
          )}
        />
      )}
    </Box>
  );
};
